import { Action } from '@reduxjs/toolkit';
import { all, call, fork, takeEvery, takeLatest, takeLeading } from 'typed-redux-saga';

import { deviceManager, goloomSdk } from '../../../shared/features/goloom-sdk';
import { setStoreShowMyVideoSettingsAction } from '../../../shared/features/settings';
import { ChannelHolder } from '../../../shared/helpers/channelHolder';
import { safe } from '../../../shared/helpers/redux-saga';
import { rootBroadcastSaga } from '../features/broadcast';
import {
    createChatAction,
    createChatSaga,
    exportChatHistorySavedAction,
    saveHistoryNotificationSaga,
    setOnlineInChat,
    setOnlineInChatAction,
    sidebarVisibilityChangeSaga,
    toggleChatVisibilityAction,
    toggleChatVisibilitySaga,
} from '../features/chat';
import { cloudRecordingSagas } from '../features/cloud-recording';
import {
    cancelCallSaga,
    fetchMeSaga,
    handleConnectionStatusChangeSaga,
    participantsSagaWatcher,
    watchConference,
} from '../features/conference';
import {
    onParticipantDisabledCamera,
    onParticipantEnabledCameraSuccess,
    onParticipantEnabledScreenSharingSuccess,
    onParticipantTurnedVideoOn,
} from '../features/conference/sagas/participants';
import {
    cancelCallAction,
    fetchMe,
    participantScreenSharingSuccess,
    participantTurnedVideoOff,
    participantTurnedVideoOn,
    participantTurnedVideoOnSuccess,
} from '../features/conference/slice';
import {
    changeAudioStatusSaga,
    changeDeviceSaga,
    changeVideoStatusSaga,
    onActiveDeviceChangedSaga,
    setDevicesQueueWatcher,
    toggleMuteSaga,
    watchDeviceChange,
    watchDevicesPermissionsSaga,
} from '../features/device';
import { toggleMuteAction } from '../features/device/slice';
import { rootFullscreenSaga } from '../features/fullscreen';
import { videoGridSagaWatcher } from '../features/goloom-video-grid';
import { onNetworkQualityChangedSaga, onToggleShowMyVideo } from '../features/network-quality';
import { onSoundProcessorOptionsChangedSaga } from '../features/noise-cancellation';
import { clearSearchQueryOnParticipantsCloseSaga } from '../features/participants-search';
import { pipGridSagaWatcher } from '../features/pip/sagas';
import { setEmotionAction, setEmotionSaga, setReactionAction, setReactionSaga } from '../features/reaction';
import {
    changeIsLocalRecordingSaga,
    rootRecordSaga as recordSagas,
    setIsLocalRecordingAction,
} from '../features/record';
import { reportAProblemSagas } from '../features/report-a-problem/sagas';
import { transcoderObservabilityRootSaga } from '../features/room-agent/transcoder-observability';
import {
    handleDisplayMediaStreamStarted,
    handleDisplayMediaStreamStopped,
    shareScreenSaga,
} from '../features/screen-sharing';
import { shareScreenAction } from '../features/screen-sharing/slice';
import { setSidebarContent } from '../features/sidebar/slice';
import { summarizationSagas } from '../features/summarization';
import { rootTalkWhileMutedSaga } from '../features/talk-while-muted';
import { wakeLockSaga } from '../features/wake-lock';
import { handleErrorSaga } from '../helpers/error';
import {
    canStartRecording,
    getAudioStreamTracks,
    getParticipantDrawerParams,
} from '../helpers/record-sagas-goloom-helpers';
import { sagaErrorHandler } from '../helpers/sagas-error-handlers';
import { rootIframeSaga } from '../iframe-sagas';
import { resetCall } from '../slice';
import { resetCallSaga } from './reset-call';

export const recordHelpers = {
    getAudioStreamTracks,
    getParticipantDrawerParams,
    canStartRecording,
};

export function* rootGoloomCall() {
    const channelHolder = new ChannelHolder();

    try {
        yield all([
            fork(watchDevicesPermissionsSaga),
            fork(watchDeviceChange),
            call(watchConference),
            call(recordSagas, recordHelpers),
            call(cloudRecordingSagas),
            call(summarizationSagas),
            call(reportAProblemSagas),
            call(rootFullscreenSaga),
            call(rootTalkWhileMutedSaga),
            call(rootBroadcastSaga),
            call(rootIframeSaga),
            takeLeading(
                (action: Action) => toggleMuteAction.match(action) && action.payload.type === 'audio',
                safe(sagaErrorHandler('toggleMuteSaga'), toggleMuteSaga)
            ),
            takeLeading(
                (action: Action) => toggleMuteAction.match(action) && action.payload.type === 'video',
                safe(sagaErrorHandler('toggleMuteSaga'), toggleMuteSaga)
            ),
            takeEvery(cancelCallAction, safe(sagaErrorHandler('cancelCallSaga'), cancelCallSaga)),
            takeEvery(fetchMe, safe(sagaErrorHandler('fetchMeSaga'), fetchMeSaga)),
            takeLeading(createChatAction, safe(sagaErrorHandler('createChatSaga'), createChatSaga)),
            takeLeading(
                toggleChatVisibilityAction,
                safe(sagaErrorHandler('toggleChatVisibilitySaga'), toggleChatVisibilitySaga)
            ),
            takeEvery(setOnlineInChatAction, safe(sagaErrorHandler('setOnlineInChat'), setOnlineInChat)),
            takeEvery(
                setSidebarContent,
                safe(sagaErrorHandler('sidebarVisibilityChangeSaga'), sidebarVisibilityChangeSaga)
            ),
            takeEvery(
                exportChatHistorySavedAction,
                safe(sagaErrorHandler('saveHistoryNotificationSaga'), saveHistoryNotificationSaga)
            ),
            takeEvery(shareScreenAction, safe(sagaErrorHandler('shareScreenSaga'), shareScreenSaga)),
            takeLatest(setIsLocalRecordingAction, changeIsLocalRecordingSaga),
            channelHolder.takeEvery(goloomSdk.onError, handleErrorSaga),
            channelHolder.takeLatest(goloomSdk.onConnectionStatusChanged, handleConnectionStatusChangeSaga),
            channelHolder.takeEvery(goloomSdk.onDisplayMediaStarted, handleDisplayMediaStreamStarted),
            channelHolder.takeEvery(goloomSdk.onDisplayMediaStopped, handleDisplayMediaStreamStopped),
            channelHolder.takeEvery(deviceManager.onAudioStatusChanged, changeAudioStatusSaga),
            channelHolder.takeEvery(deviceManager.onVideoStatusChanged, changeVideoStatusSaga),
            channelHolder.takeEvery(deviceManager.onDeviceChanged, changeDeviceSaga),
            channelHolder.takeEvery(deviceManager.onActiveDevicesChanged, onActiveDeviceChangedSaga),
            channelHolder.takeEvery(goloomSdk.onNoiseCancellationTypeChanged, onSoundProcessorOptionsChangedSaga),
            channelHolder.takeLatest(goloomSdk.onNetworkQualityChanged, onNetworkQualityChangedSaga),
            call(transcoderObservabilityRootSaga),
            takeLatest(setStoreShowMyVideoSettingsAction, onToggleShowMyVideo),
            fork(wakeLockSaga),
            takeLatest(setReactionAction, setReactionSaga),
            takeLatest(setEmotionAction, setEmotionSaga),
            takeEvery(resetCall, resetCallSaga),
            call(participantsSagaWatcher),
            call(videoGridSagaWatcher),
            call(pipGridSagaWatcher),
            takeEvery(setSidebarContent, clearSearchQueryOnParticipantsCloseSaga),
            fork(setDevicesQueueWatcher),
            takeEvery(participantTurnedVideoOn, onParticipantTurnedVideoOn),
            takeEvery(participantTurnedVideoOnSuccess, onParticipantEnabledCameraSuccess),
            takeEvery(participantTurnedVideoOff, onParticipantDisabledCamera),
            takeEvery(participantScreenSharingSuccess, onParticipantEnabledScreenSharingSuccess),
        ]);
    } finally {
        channelHolder.close();
    }
}
